import { create } from 'zustand'
import type { DashboardData } from '@/types'
import * as parceirosApi from '@/api/parceiros'

export type PeriodOption = 'este-mes' | 'mes-passado' | 'ultimos-30-dias' | 'personalizado'
export type DashboardMetric = 'receita' | 'matriculas'
export type ComparisonType = 'periodo-anterior' | 'mes-anterior' | 'ano-anterior'

const METRIC_STORAGE_KEY = 'parceirosDashboardMetric'

interface DashboardState {
  data: DashboardData | null
  loading: boolean
  error: string | null
  fixedLoaded: boolean
  periodOption: PeriodOption
  customRange: [string, string] | null
  comparisonType: ComparisonType
  metric: DashboardMetric
  setPeriodOption: (option: PeriodOption) => void
  setCustomRange: (range: [string, string] | null) => void
  setComparisonType: (type: ComparisonType) => void
  setMetric: (metric: DashboardMetric) => void
  fetchDashboard: () => Promise<void>
}

function parseLocalDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, m - 1, d)
}

function formatDate(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function resolveIsoRange(option: PeriodOption, customRange: [string, string] | null): [string, string] {
  const now = new Date()
  const year = now.getFullYear()
  const month = now.getMonth()

  if (option === 'este-mes') {
    return [formatDate(new Date(year, month, 1)), formatDate(new Date(year, month + 1, 0))]
  }

  if (option === 'mes-passado') {
    return [formatDate(new Date(year, month - 1, 1)), formatDate(new Date(year, month, 0))]
  }

  if (option === 'ultimos-30-dias') {
    const end = new Date(now)
    const start = new Date(now)
    start.setDate(start.getDate() - 29)
    return [formatDate(start), formatDate(end)]
  }

  return customRange ?? [formatDate(new Date(year, month, 1)), formatDate(new Date(year, month + 1, 0))]
}

function resolveComparisonRange(
  option: PeriodOption,
  periodStart: string,
  periodEnd: string,
  comparisonType: ComparisonType,
): [string, string] {
  if (option === 'este-mes' || option === 'mes-passado') {
    // Previous calendar month: one day before periodStart → start of that month
    const pStart = parseLocalDate(periodStart)
    const compEnd = new Date(pStart)
    compEnd.setDate(compEnd.getDate() - 1)
    const compStart = new Date(compEnd.getFullYear(), compEnd.getMonth(), 1)
    return [formatDate(compStart), formatDate(compEnd)]
  }

  if (option === 'ultimos-30-dias') {
    // 30 days immediately before the period
    const pStart = parseLocalDate(periodStart)
    const compEnd = new Date(pStart)
    compEnd.setDate(compEnd.getDate() - 1)
    const compStart = new Date(compEnd)
    compStart.setDate(compStart.getDate() - 29)
    return [formatDate(compStart), formatDate(compEnd)]
  }

  // personalizado
  const pStart = parseLocalDate(periodStart)
  const pEnd = parseLocalDate(periodEnd)

  if (comparisonType === 'mes-anterior') {
    const compStart = new Date(pStart.getFullYear(), pStart.getMonth() - 1, pStart.getDate())
    const compEnd = new Date(pEnd.getFullYear(), pEnd.getMonth() - 1, pEnd.getDate())
    return [formatDate(compStart), formatDate(compEnd)]
  }

  if (comparisonType === 'ano-anterior') {
    const compStart = new Date(pStart.getFullYear() - 1, pStart.getMonth(), pStart.getDate())
    const compEnd = new Date(pEnd.getFullYear() - 1, pEnd.getMonth(), pEnd.getDate())
    return [formatDate(compStart), formatDate(compEnd)]
  }

  // periodo-anterior: same number of days, immediately before
  const days = Math.round((pEnd.getTime() - pStart.getTime()) / (24 * 60 * 60 * 1000)) + 1
  const compEnd = new Date(pStart)
  compEnd.setDate(compEnd.getDate() - 1)
  const compStart = new Date(compEnd)
  compStart.setDate(compStart.getDate() - (days - 1))
  return [formatDate(compStart), formatDate(compEnd)]
}

function loadStoredMetric(): DashboardMetric {
  const stored = localStorage.getItem(METRIC_STORAGE_KEY)
  return stored === 'matriculas' ? 'matriculas' : 'receita'
}

export const useDashboardStore = create<DashboardState>((set, get) => ({
  data: null,
  loading: false,
  error: null,
  fixedLoaded: false,
  periodOption: 'este-mes',
  customRange: null,
  comparisonType: 'periodo-anterior',
  metric: loadStoredMetric(),

  setPeriodOption: (option) => {
    set({ periodOption: option })
    get().fetchDashboard()
  },

  setCustomRange: (range) => {
    set({ customRange: range })
    if (get().periodOption === 'personalizado') {
      get().fetchDashboard()
    }
  },

  setComparisonType: (type) => {
    set({ comparisonType: type })
    if (get().periodOption === 'personalizado') {
      get().fetchDashboard()
    }
  },

  setMetric: (metric) => {
    localStorage.setItem(METRIC_STORAGE_KEY, metric)
    set({ metric })
  },

  fetchDashboard: async () => {
    const { periodOption, customRange, comparisonType, fixedLoaded, data: existingData } = get()
    const [periodStart, periodEnd] = resolveIsoRange(periodOption, customRange)
    const [comparisonStart, comparisonEnd] = resolveComparisonRange(periodOption, periodStart, periodEnd, comparisonType)
    set({ loading: true, error: null })
    try {
      const freshData = await parceirosApi.getDashboard(periodStart, periodEnd, comparisonStart, comparisonEnd)
      set({
        data: fixedLoaded && existingData
          ? {
              ...freshData,
              mesAtualCards: existingData.mesAtualCards,
              pendencias: existingData.pendencias,
              proximasConcluir: existingData.proximasConcluir,
              hasMarkupHistory: existingData.hasMarkupHistory,
            }
          : freshData,
        fixedLoaded: true,
      })
    } catch {
      set({ error: 'Erro ao carregar o painel. Verifique sua conexão e tente novamente.' })
    } finally {
      set({ loading: false })
    }
  },
}))
